import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import {
  AwBadgeComponent,
  AwTableComponent,
  AwDividerComponent,
  AwActionBarComponent,
  ActionBarRight,
  SearchOption,
  SingleSelectOption,
  TableRow,
  AwSpinnerComponent,
  AwDialogComponent,
  DialogFormControls,
  DialogOptions,
  AwToastComponent,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import {
  TableCellInput,
  TableCellTypes,
} from '@assetworks-llc/aw-component-lib';

import { AwSearchComponent } from '@assetworks-llc/aw-component-lib';
import { AwButtonDirective } from '@assetworks-llc/aw-component-lib';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { AwDropdownFormComponent } from '../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { CustomerService } from '../../../../core/services/customer.service';
import { UpdateOrganizationParams } from '../../../../types/types';
import { ThemeService } from '../../../../core/services/theme.service';
import {
  catchError,
  concat,
  finalize,
  forkJoin,
  last,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { SecretService } from '../../../../core/services/secret.service';
import { CommunicationPropertiesService } from '../../../../core/services/communication-properties.service';
import { IntegrationService } from '../../../../core/services/integration.service';
import {
  Organization,
  UserInfo,
  UserService,
} from '../../../../core/services/user.service';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { Customer, Integration } from '../../../../api/models';

@Component({
  selector: 'app-client-management',
  imports: [
    CommonModule,
    FormsModule,
    AwTableComponent,
    AwDividerComponent,
    AwSearchComponent,
    AwButtonDirective,
    AwActionBarComponent,
    AwDropdownFormComponent,
    AwDialogComponent,
    AwSpinnerComponent,
    AwToastComponent,
    ReactiveFormsModule,
    MatIcon,
  ],
  templateUrl: './customer-management.component.html',
  styleUrls: [
    './customer-management.component.scss',
    '../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class CustomerManagementComponent implements OnInit {
  constructor(
    private router: Router,
    private customerService: CustomerService,
    private integrationService: IntegrationService,
    private themeService: ThemeService,
    private authenticationService: AuthenticationService,
    private secretService: SecretService,
    private communicationPropService: CommunicationPropertiesService,
    private userService: UserService,
    private parentIntegrationService: ParentIntegrationService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  container_theme = computed(() => this.themeService.themeSignal());

  organization: Organization | null = null;
  organizationId: string | null = null;

  user: UserInfo | null = null;
  userPermissions: Record<string, boolean> = {};

  openChangeStatusDialog: boolean = false;
  openDeleteCustomerDialog: boolean = false;

  disableChangeStatus: boolean = true;

  customerToDelete: TableRow | null = null;

  checkedRows: TableRow[] = [];
  isLoading: boolean = false;
  customerStatus: string | undefined = '';
  searchInputValue!: string;
  selectionOptions: SearchOption[] = [];
  title = input<string>('Expiry Date Range');
  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];
  enableStickyOptions = signal(false);
  sortDisabled = signal(false);

  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  formFieldsData = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Customer`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  dialogOptionsStatus = input<DialogOptions>({
    variant: 'standard',
    title: 'Change Status',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  });

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  private fetchCustomersFromIds(customerIds: string[]): Observable<Customer[]> {
    if (!customerIds.length) return of([]);

    return forkJoin(
      customerIds.map(id =>
        this.customerService.getCustomerById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(customers => customers.filter((c): c is Customer => c !== null))
    );
  }

  private fetchIntegrationsFromIds(integrationIds: string[]): Observable<Integration[]> {
    if (!integrationIds.length) return of([]);

    return forkJoin(
      integrationIds.map(id =>
        this.integrationService.getIntegrationById({ organizationId: id })
          .pipe(catchError(() => of(null)))
      )
    ).pipe(
      map(integrations => integrations.filter((i): i is Integration => i !== null))
    );
  }

  buildTableAllCustomers() {
    this.customerService
      .getAllCustomers()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        catchError((error) => {
          this.buildToastMessage('Failed to fetch customer data', 'error');
          return of([]);
        })
      )
      .subscribe((customers) => {
        const customerData: any[] =
          customers?.map((customer) => ({
            customerInfo: {
              customerAuthType: customer.authenticationType,
              customerId: customer.organizationId ?? 'N/A',
              customerName: customer.organizationName ?? 'Not Available',
              customerIntegrationPartnerIDs: customer.integrationPartnerIds,
              detailsPageRoute: `/customer-management/customer-details/${customer.organizationId}`,
            },
            badgeNested: {
              enabled: customer.enabled,
              color: customer.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: customer.enabled ? 'Active' : 'Disabled',
            },
            integrations: customer.integrationPartnerIds?.length ?? 0,
            modified_date: customer.modifiedOn
              ? new Date(customer.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_added: customer.createdOn
              ? new Date(customer.createdOn).toLocaleDateString()
              : 'N/A',
            checkbox: false,
          })) ?? [];

        this.tableData.set(customerData);
        this.tableDataCopy = customerData;
      });
  }

  buildTableIntegrationCustomers() {
    this.parentIntegrationService
      .getParentIntegrationById({ organizationId: this.organizationId ?? '' })
      .pipe(
        switchMap((parentIntegration) => {
          if (!parentIntegration?.integrationIds?.length) {
            return of([]);
          }

          return this.fetchIntegrationsFromIds(parentIntegration.integrationIds).pipe(
            switchMap(integrations => {
              const allCustomerIds = new Set<string>();
              integrations.forEach(integration =>
                (integration.customerIds || []).forEach(id => allCustomerIds.add(id))
              );

              return this.fetchCustomersFromIds(Array.from(allCustomerIds));
            })
          );
        }),
        catchError(() => {
          return this.integrationService
            .getIntegrationById({ organizationId: this.organizationId ?? '' })
            .pipe(
              catchError(() => of(null)),
              switchMap(integration =>
                this.fetchCustomersFromIds(integration?.customerIds || [])
              )
            );
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((customers) => {
        const integrationCustomersData: any[] =
          customers?.map((customer) => ({
            customerInfo: {
              customerAuthType: customer.authenticationType,
              customerId: customer.organizationId ?? 'N/A',
              customerName: customer.organizationName ?? 'Not Available',
              customerIntegrationPartnerIDs: customer.integrationPartnerIds,
              detailsPageRoute: `/customer-management/customer-details/${customer.organizationId}`,
            },
            badgeNested: {
              enabled: customer.enabled,
              color: customer.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: customer.enabled ? 'Active' : 'Disabled',
            },
            integrations: customer.integrationPartnerIds?.length ?? 0,
            modified_date: customer.modifiedOn
              ? new Date(customer.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_added: customer.createdOn
              ? new Date(customer.createdOn).toLocaleDateString()
              : 'N/A',
            checkbox: false,
          })) ?? [];

        this.tableData.set(integrationCustomersData);
        this.tableDataCopy = integrationCustomersData;
      });
  }

  ngOnInit() {
    this.isLoading = true;

    this.user = this.userService.getUserInfo();
    this.userPermissions = this.userService.getUserPermissions();

    this.organization = this.userService.getOrganization();
    this.organizationId = this.user?.organizationId || null;

    if (this.userPermissions['canDelete'] || this.userPermissions['canEdit']) {
      this.columnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.userPermissions['canDelete']) {
      this.columnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'random',
        label: 'Remove',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: TableRow) => {
          this.openDeleteCustomerDialog = true;
          this.customerToDelete = data;
        },
      });
    }

    if (this.organization === 'assetworks-employee') {
      this.buildTableAllCustomers();
    } else if (this.organization === 'integration-employee') {
      this.buildTableIntegrationCustomers();
    }
  }

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'customerInfo',
      label: 'Customer Name/ID',
      combineFields: [
        'customerInfo.customerName',
        'customerInfo.detailsPageRoute',
        'customerInfo.customerId',
      ],
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'integrations',
      label: 'Number of Integrations',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'date_added',
      label: 'Date Added',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];
  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  <p>${values[2]}</p>
  </div>`;

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Add Customer',
        buttonType: 'outlined',
        action: () =>
          this.router.navigate(['/customer-management/add-customer']),
      },
    },
  ]);

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    if (this.checkedRows.length > 0) {
      this.disableChangeStatus = false;
    } else {
      this.disableChangeStatus = true;
    }
  }
  onCustomerStatusSelected(status: SingleSelectOption | null) {
    if (status === null) {
      this.awTableComponent.applyFilter('');
      return;
    }
    this.awTableComponent.applyFilter(status!.label);
  }
  onSearchInput(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }
  dialogFormValuesHandler(formValues: any, key: string) {
    if (key === 'status') {
      if (formValues && formValues.valid) {
        this.onChangeStatus(formValues.formData.changeStatus.value);
      }
    } else if (key === 'delete') {
      if (this.customerToDelete) {
        this.onDeleteCustomer(this.customerToDelete['customerInfo'].customerId);
      }
    }
  }
  dialogCloseHandler(key: string) {
    if (key === 'status') {
      this.openChangeStatusDialog = false;
    } else if (key === 'delete') {
      this.openDeleteCustomerDialog = false;
    }
  }

  onDeleteCustomer(customerId: string) {
    this.isLoading = true;

    this.secretService
      .listAllSecrets({ organizationId: customerId })
      .pipe(
        mergeMap((secrets) => {
          if (!secrets || secrets.length === 0) {
            return of([]);
          }
          return forkJoin(
            secrets.map((secret) =>
              this.secretService
                .deleteSecret({
                  organizationId: customerId,
                  secretName: secret.name ?? '',
                })
                .pipe(catchError(() => of(false)))
            )
          );
        }),
        catchError(() => of([])),
        mergeMap(() =>
          this.communicationPropService
            .getCommunicationPropertiesById({ organizationId: customerId })
            .pipe(
              mergeMap(() =>
                this.communicationPropService.deleteCommunicationProperties({ organizationId: customerId })
              ),
              catchError(() => of(false))
            )
        ),
        mergeMap(() =>
          this.authenticationService
            .getAuthenticationConfig({ organizationId: customerId })
            .pipe(
              mergeMap(() =>
                this.authenticationService.deleteAuthenticationConfig({ organizationId: customerId })
              ),
              catchError(() => of(false))
            )
        ),
        mergeMap(() =>
          this.customerService.deleteCustomer({ organizationId: customerId })
        ),
        finalize(() => {
          this.isLoading = false;
          this.openDeleteCustomerDialog = false;
          this.customerToDelete = null;
        })
      )
      .subscribe({
        next: (deleted) => {
          const filtered = this.tableDataCopy.filter(
            (row) => row['customerInfo'].customerId !== customerId
          );
          this.tableData.set(filtered);
          this.tableDataCopy = [...filtered];
          this.buildToastMessage('Successfully deleted customer', 'success');
        },
        error: () => {
          this.buildToastMessage('Failed to delete customer', 'error');
        }
      });
  }

  onChangeStatus(status: boolean) {
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }
    const customersToChange = this.checkedRows.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    if (!customersToChange.length) {
      this.buildToastMessage(
        `No ${!status ? 'active' : 'disabled'} rows selected`,
        'alert'
      );
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = customersToChange.map(
      (customer) => {
        const info = customer['customerInfo'];
        const statusInfo = customer['badgeNested'];
        const customerParams: UpdateOrganizationParams = {
          organizationAuthType: info.customerAuthType,
          organizationName: info.customerName,
          organizationStatus: !statusInfo.enabled,
          organizationPartnerIds: info.customerIntegrationPartnerIDs,
          organizationId: info.customerId,
        };

        return this.customerService
          .updateCustomer(customerParams)
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeStatusDialog = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates: number = results.filter(
          (updated) => updated
        ).length;

        customersToChange.forEach((customer, index) => {
          if (results[index]) {
            const badge = customer['badgeNested'];

            badge.enabled = status;
            badge.color = badge.enabled
              ? 'success-green-700'
              : 'aw-dark-gray-1000';
            badge.label = badge.enabled ? 'Active' : 'Disabled';
          }
        });

        if (numStatusUpdates === customersToChange.length) {
          this.buildToastMessage(
            `Successfully ${
              status ? 'enabled' : 'disabled'
            } all selected customers`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `ALERT: ${numStatusUpdates} of ${
              customersToChange.length
            } customers ${status ? 'enabled' : 'disabled'}`,
            'alert'
          );
        } else {
          this.buildToastMessage(
            `Failed to ${status ? 'enable' : 'disable'} any selected customers`,
            'error'
          );
        }
      });
  }
}
